import type { AgentVersionInput } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import type {
	Conversation,
	StoreAssistant,
	StoreChatItem,
	StoreHuman,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';
import { trimToUndefined } from '@atlassian/conversation-assistant-utils/default-agent';

import { findPreviousByTypeFromId } from './findPreviousByTypeFromId';

/**
 * Resolves the agent version for a confirmed-tools request routed to the agent that authored
 * the originating assistant message. Every candidate must belong to that recipient, so a
 * version belonging to another agent in the conversation is never attached:
 *
 * 1. the originating message's `agent_author` version;
 * 2. the draft version of the request that produced the message (draft-agent replies persist a
 *    null `agent_author`, so this is the only source during the session);
 * 3. the conversation's version, when the recipient is the conversation's own agent.
 */
export const resolveConfirmedToolsAgentVersion = ({
	messages,
	originatingMessage,
	recipientAgentId,
	conversation,
}: {
	messages: StoreChatItem[];
	originatingMessage: StoreAssistant | undefined;
	recipientAgentId: string | undefined;
	conversation: Pick<Conversation, 'agent' | 'agentVersion' | 'sourceAgentId'> | undefined;
}): AgentVersionInput | undefined => {
	const recipientId = trimToUndefined(recipientAgentId);
	if (!recipientId) {
		return undefined;
	}

	if (originatingMessage) {
		const { id: messageId, agent_author: agentAuthor } = originatingMessage.content.message.message;
		if (
			trimToUndefined(agentAuthor?.id) === recipientId &&
			(agentAuthor?.versionId || agentAuthor?.versionType)
		) {
			return { versionId: agentAuthor.versionId, versionType: agentAuthor.versionType };
		}

		const requestAgentIdentity = findPreviousByTypeFromId<StoreHuman>({
			messages,
			messageId,
			searchType: 'HUMAN',
		})?.followUpAgentIdentity;
		if (
			requestAgentIdentity?.id === recipientId &&
			requestAgentIdentity.agentVersion?.versionType === 'DRAFT'
		) {
			return requestAgentIdentity.agentVersion;
		}
	}

	const conversationAgentId = trimToUndefined(
		conversation?.agent?.agent_id ?? conversation?.sourceAgentId,
	);
	return conversationAgentId === recipientId ? conversation?.agentVersion : undefined;
};
